import PublicOffRoundedIcon from "@mui/icons-material/PublicOffRounded";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import { Box, IconButton, MenuItem, Select, Typography } from "@mui/material";
import { useState } from "react";
import useColors from "../../../../../../api/hooks/useColors";
import CustomToolTip from "../../../../../../components/CustomTooltip";
import SvgToIcon from "../../../../../../icons/SvgToIcon";
import { getPrettyDateTime } from "../../../../../../utils/timestamp";
import SaveViewModal from "../../../components/SaveViewModal";
import { icons } from "../../../icons/iconExport";
import useInventoryViews from "../hooks/useInventoryViews";

export default function InventoryViewControls({
  appliedFilters,
  setAppliedFilters,
  entityFilters,
  setEntityFilters,
  entityFilterLogic,
  setEntityFilterLogic,
  setPostgresFilteredEntities,
  setIsSearching,
  handleToggleTableView,
  visibleColumns,
  setVisibleColumns,
}) {
  const colors = useColors();
  const [showSaveViewModal, setShowSaveViewModal] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  const {
    inventoryViews,
    selectedViewId,
    handleViewSelect,
    handleSaveView,
    handleUpdateView,
    saveViewMutation,
    updateViewMutation,
    isLoadingView,
  } = useInventoryViews({
    appliedFilters,
    setAppliedFilters,
    entityFilters,
    setEntityFilters,
    entityFilterLogic,
    setEntityFilterLogic,
    setPostgresFilteredEntities,
    setIsSearching,
    handleToggleTableView,
    visibleColumns,
    setVisibleColumns,
  });

  const selectedView = inventoryViews.find((v) => v.view_id === selectedViewId);

  const handleSaveClick = () => {
    setIsUpdateMode(false);
    setShowSaveViewModal(true);
  };

  const handleUpdateClick = () => {
    setIsUpdateMode(true);
    setShowSaveViewModal(true);
  };

  const handleCloseModal = () => {
    setShowSaveViewModal(false);
    setIsUpdateMode(false);
  };

  const handleSaveViewWrapper = async (viewData) => {
    if (isUpdateMode && selectedViewId) {
      const selectedView = inventoryViews.find(
        (v) => v.view_id === selectedViewId
      );
      await handleUpdateView(
        selectedViewId,
        selectedView.customer_id,
        viewData,
        selectedView.is_public
      );
    } else {
      await handleSaveView(viewData);
    }
    handleCloseModal();
  };

  if (inventoryViews.length === 0) {
    return (
      <Box display="flex">
        <CustomToolTip title="Save New View" placement="top">
          <IconButton
            sx={{
              background: colors.common_white,
              color: colors.action_active,
              border: "0.5px solid " + colors.grey_48percent,

              height: "32px",
              width: "32px",
              borderRadius: "8px",
              textTransform: "none",
              padding: "4px",
              whiteSpace: "nowrap",
              "&:hover": {
                background: colors.grey_8percent,
              },
            }}
            onClick={handleSaveClick}
          >
            <SvgToIcon
              IconLogo={icons.SaveIcon}
              style={{ width: "18px", color: colors.icon_action_active }}
            />
          </IconButton>
        </CustomToolTip>
        <SaveViewModal
          open={showSaveViewModal}
          onClose={handleCloseModal}
          onSave={handleSaveViewWrapper}
          isLoading={saveViewMutation.isPending}
          existingView={null}
        />
      </Box>
    );
  }

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        width="100%"
        sx={{
          border: "0.5px solid " + colors.grey_48percent,
          padding: "0px 4px 0px 0px",
          borderRadius: "10px",
        }}
      >
        <Select
          value={selectedViewId}
          onChange={(e) => handleViewSelect(e.target.value)}
          displayEmpty
          aria-label="Select View"
          size="small"
          disabled={isLoadingView}
          sx={{
            borderRadius: "10px",
            minWidth: "200px",
            border: `1px solid ${colors.grey_20percent}`,
            height: "32px",

            "& .MuiSelect-select": {
              padding: "8px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&.Mui-focused .MuiOutlinedInput-root": {
              boxShadow: "none",
            },
          }}
        >
          <MenuItem value="">
            <Typography variant="caption" color={colors.text_secondary}>
              Select View
            </Typography>
          </MenuItem>
          {inventoryViews.map((view) => (
            <MenuItem key={view.view_id} value={view.view_id}>
              <CustomToolTip
                title={
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Typography variant="caption" color={colors.text_primary}>
                      <b>Created on</b>{" "}
                      {getPrettyDateTime(view.created_at.timestamp_utc)}
                    </Typography>
                    <Typography variant="caption" color={colors.text_primary}>
                      <b>Created by</b> {view.created_by}
                    </Typography>
                  </Box>
                }
                placement="top"
              >
                <Box
                  width="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="caption" color={colors.text_primary}>
                    {view.name}
                  </Typography>
                  {view.is_public ? (
                    <CustomToolTip title="Public view" placement="top">
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{
                          padding: "4px",
                          borderRadius: "4px",
                          backgroundColor: colors.primary_16percent,
                        }}
                      >
                        <PublicOutlinedIcon
                          style={{
                            color: colors.primary_main,
                            width: "16px",
                            height: "16px",
                          }}
                        />
                      </Box>
                    </CustomToolTip>
                  ) : (
                    <CustomToolTip title="Private view" placement="top">
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        sx={{
                          padding: "4px",
                          borderRadius: "4px",
                          backgroundColor: colors.primary_16percent,
                        }}
                      >
                        <PublicOffRoundedIcon
                          style={{
                            color: colors.primary_main,
                            width: "16px",
                            height: "16px",
                          }}
                        />
                      </Box>
                    </CustomToolTip>
                  )}
                </Box>
              </CustomToolTip>
            </MenuItem>
          ))}
        </Select>
        {selectedViewId && (
          <CustomToolTip title="Reset Selected View" placement="top">
            <IconButton
              sx={{
                background: colors.common_white,
                color: colors.action_active,
                height: "32px",
                width: "32px",
                borderRadius: "8px",
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  background: colors.grey_8percent,
                },
              }}
              onClick={() => handleViewSelect("")}
            >
              <SvgToIcon
                IconLogo={icons.CloseIcon}
                style={{ width: "20px", color: colors.icon_action_active }}
              />
            </IconButton>
          </CustomToolTip>
        )}
        {selectedViewId && (
          <CustomToolTip title="Update View" placement="top">
            <IconButton
              sx={{
                background: colors.common_white,
                color: colors.action_active,
                height: "32px",
                width: "32px",
                borderRadius: "8px",
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  background: colors.grey_8percent,
                },
              }}
              onClick={handleUpdateClick}
            >
              <SvgToIcon
                IconLogo={icons.UpdateIcon}
                style={{ width: "20px", color: colors.icon_action_active }}
              />
            </IconButton>
          </CustomToolTip>
        )}
        <CustomToolTip title="Save New View" placement="top">
          <IconButton
            sx={{
              background: colors.common_white,
              color: colors.action_active,
              height: "32px",
              width: "32px",
              borderRadius: "8px",
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": {
                background: colors.grey_8percent,
              },
            }}
            onClick={handleSaveClick}
          >
            <SvgToIcon
              IconLogo={icons.SaveIcon}
              style={{ width: "18px", color: colors.icon_action_active }}
            />
          </IconButton>
        </CustomToolTip>
      </Box>

      <SaveViewModal
        open={showSaveViewModal}
        onClose={handleCloseModal}
        onSave={handleSaveViewWrapper}
        isLoading={saveViewMutation.isPending || updateViewMutation.isPending}
        existingView={isUpdateMode ? selectedView : null}
      />
    </>
  );
}
